import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';

import { BlobServiceClient } from '@azure/storage-blob';

import { ToastService } from '../../../Services/toast/toast.service';
import { ConnectiveservicesService } from '../../../Services/Apis/Connectiveservices/connectiveservices.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateDirective } from '../../Utilites/Directives/translate.directive';

@Component({
  selector: 'app-dynamic-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    TranslateDirective,
    MatDialogModule,
  ],
  templateUrl: './dynamic-file-upload.component.html',
  styleUrls: ['./dynamic-file-upload.component.scss'],
})
export class DynamicFileUploadComponent implements OnInit {
  @Input() allowedFileTypes =
    '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png';

  @Input() maxFileSizeMB = 10;

  @Output() cancel = new EventEmitter<void>();

  @Output() formSubmit = new EventEmitter<{
    files_list?: { content: string }[];
    links_list?: string[];
  }>();

  uploadForm: FormGroup;

  selectedFiles: { file: File; title: string }[] = [];

  isDragging = false;
  isLink = false;
  isUploading = false;

  accessUrl = '';
  containerName = '';
  FolderDataPath = '';

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<DynamicFileUploadComponent>,
    private toast: ToastService,
    private connectiveservices: ConnectiveservicesService,
  ) {
    this.uploadForm = this.fb.group({
      linkList: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.isLink = !!this.data?.isLink;
    this.accessUrl = this.data?.accessUrl || '';
    this.containerName = this.data?.containerName || '';
    this.FolderDataPath = this.data?.folderDataPath || '';

    if (this.isLink && this.linkList.length === 0) {
      this.addLinkInput();
    }
  }

  get linkList(): FormArray {
    return this.uploadForm.get('linkList') as FormArray;
  }

  private createLinkGroup(): FormGroup {
    return this.fb.group({
      link: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(https?:\/\/[^\s$.?#].[^\s]*)$/i),
        ],
      ],
    });
  }

  addLinkInput(): void {
    this.linkList.push(this.createLinkGroup());
  }

  removeLinkInput(index: number): void {
    this.linkList.removeAt(index);

    if (this.linkList.length === 0) {
      this.addLinkInput();
    }
  }

  getErrorMessage(control: AbstractControl | null, label: string): string {
    if (!control?.errors) return '';

    if (control.hasError('required')) return `${label} is required`;

    if (control.hasError('pattern')) return 'Invalid format';

    return 'Invalid value';
  }

  // ----------------------------------------------------
  // DRAG DROP
  // ----------------------------------------------------

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;

    if (event.dataTransfer?.files) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files) {
      this.addFiles(Array.from(input.files));
    }
  }

  // ----------------------------------------------------
  // FILE ADD
  // ----------------------------------------------------

  private addFiles(files: File[]): void {
    const maxSizeBytes = this.maxFileSizeMB * 1024 * 1024;

    files.forEach((file) => {
      if (file.size > maxSizeBytes) {
        this.toast.showToast(
          'warning',
          `${file.name} exceeds ${this.maxFileSizeMB} MB`,
        );
        return;
      }

      this.selectedFiles.push({
        file,
        title: file.name,
      });
    });
  }

  removeSelectedFile(index: number, event: Event): void {
    event.stopPropagation();
    this.selectedFiles.splice(index, 1);
  }

  // ----------------------------------------------------
  // AZURE UPLOAD
  // ----------------------------------------------------

  private sanitizeFileName(name: string): string {
    return name
      .replace(/\s+/g, ' ')
      .replace(/[^a-zA-Z0-9._ -]/g, '')
      .trim();
  }

  private async uploadFileToAzure(
    file: File,
    folderPath: string,
  ): Promise<string | null> {
    try {
      const blobService = new BlobServiceClient(this.accessUrl);

      const containerClient = blobService.getContainerClient(
        this.containerName,
      );

      const cleanFileName = this.sanitizeFileName(file.name);

      const blobName = `${folderPath}/${cleanFileName}`.replace(/^\/+/, '');

      const blockBlobClient = containerClient.getBlockBlobClient(blobName);

      await blockBlobClient.uploadBrowserData(file, {
        blobHTTPHeaders: {
          blobContentType: file.type,
        },
      });

      const basePath =
        this.connectiveservices.get_selected_sub_organization()
          ?.sub_organization_short_name || 'default';
      const trimmedPath = blobName
        .replace(basePath + '/', '')
        .replace(/^\/+/, '');

      return trimmedPath;
    } catch (error) {
      console.error('Upload error', error);
      return null;
    }
  }

  // ----------------------------------------------------
  // SUBMIT
  // ----------------------------------------------------

  async onSubmit(): Promise<void> {
    if (this.isLink) {
      if (this.linkList.invalid) {
        this.linkList.markAllAsTouched();
        this.toast.showToast('warning', 'Please fill all link fields');
        return;
      }
      this.isUploading = true;

      const links = this.linkList.value
        .map((item: any) => item.link?.trim())
        .filter((link: string) => !!link);

      this.formSubmit.emit({
        links_list: links,
      });
      this.isUploading = false;
      this.dialogRef.close();
      return;
    }

    // FILE MODE

    if (this.selectedFiles.length === 0) {
      this.toast.showToast('warning', 'Please select files');

      return;
    }

    this.isUploading = true;

    const org = this.connectiveservices.get_selected_sub_organization();

    const basePath = org?.sub_organization_short_name || 'default';

    const folderPath = `${basePath}${this.FolderDataPath || ''}`.replace(
      /\/$/,
      '',
    );

    const uploadPromises = this.selectedFiles.map(async (item) => {
      const url = await this.uploadFileToAzure(item.file, folderPath);

      return url
        ? {
            title: item.title.trim(),
            content: url,
          }
        : null;
    });

    const results = await Promise.all(uploadPromises);

    const successful = results.filter(
      (r): r is { title: string; content: string } => r !== null,
    );
    this.isUploading = false;

    if (successful.length === 0) {
      this.toast.showToast('danger', 'Upload failed');

      return;
    }

    this.formSubmit.emit({
      files_list: successful,
    });

    this.dialogRef.close();
  }
  onCancel(): void {
    this.dialogRef.close();
  }
}
